import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getDomain, createDomain, updateDomain } from '../../api/data';

const WIDGET_BASE_URL = 'https://chatbot.najahapps.com/widget';
const HOSTED_BASE_URL = 'https://chatbot.najahapps.com/c';

const generateSlug = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const DomainForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    name_ar: '',
    name_en: '',
    system_prompt_override_ar: '',
    system_prompt_override_en: '',
    widget_title_ar: '',
    widget_title_en: '',
    widget_subtitle_ar: '',
    widget_subtitle_en: '',
    primary_color: '',
    faculty_code: '',
    is_active: true,
    hero_title_ar: '',
    hero_title_en: '',
    hero_subtitle_ar: '',
    hero_subtitle_en: '',
    // Suggested questions are stored on the backend as string[] but edited
    // here as a multiline textarea — one question per line is the most
    // direct UI; we split on save and rejoin on load.
    suggested_questions_ar_text: '',
    suggested_questions_en_text: '',
    logo_url: '',
    hosted_page_enabled: true,
    // Contact card the widget shows when the bot can't answer a question.
    handoff_unit_ar: '',
    handoff_unit_en: '',
    handoff_phone: '',
    handoff_email: '',
  });

  useEffect(() => {
    if (isEdit && id) {
      loadDomain(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadDomain = async (domainId: number) => {
    try {
      const domain = await getDomain(domainId);
      setFormData({
        slug: domain.slug,
        name_ar: domain.name_ar,
        name_en: domain.name_en,
        system_prompt_override_ar: domain.system_prompt_override_ar || '',
        system_prompt_override_en: domain.system_prompt_override_en || '',
        widget_title_ar: domain.widget_title_ar || '',
        widget_title_en: domain.widget_title_en || '',
        widget_subtitle_ar: domain.widget_subtitle_ar || '',
        widget_subtitle_en: domain.widget_subtitle_en || '',
        primary_color: domain.primary_color || '',
        faculty_code: domain.faculty_code != null ? String(domain.faculty_code) : '',
        is_active: domain.is_active,
        hero_title_ar: domain.hero_title_ar || '',
        hero_title_en: domain.hero_title_en || '',
        hero_subtitle_ar: domain.hero_subtitle_ar || '',
        hero_subtitle_en: domain.hero_subtitle_en || '',
        suggested_questions_ar_text: (domain.suggested_questions_ar || []).join('\n'),
        suggested_questions_en_text: (domain.suggested_questions_en || []).join('\n'),
        logo_url: domain.logo_url || '',
        hosted_page_enabled: domain.hosted_page_enabled !== false,
        handoff_unit_ar: domain.handoff_contact?.unit_ar || '',
        handoff_unit_en: domain.handoff_contact?.unit_en || '',
        handoff_phone: domain.handoff_contact?.phone || '',
        handoff_email: domain.handoff_contact?.email || '',
      });
      setSlugManuallyEdited(true);
    } catch (error) {
      alert('Failed to load domain');
      navigate('/data/domains');
    } finally {
      setLoading(false);
    }
  };

  const handleNameEnChange = (value: string) => {
    const updates: any = { name_en: value };
    if (!slugManuallyEdited) {
      updates.slug = generateSlug(value);
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload: any = {
        slug: formData.slug,
        name_ar: formData.name_ar,
        name_en: formData.name_en,
        is_active: formData.is_active,
      };

      const optionalFields = [
        'system_prompt_override_ar', 'system_prompt_override_en',
        'widget_title_ar', 'widget_title_en',
        'widget_subtitle_ar', 'widget_subtitle_en',
        'primary_color',
        'hero_title_ar', 'hero_title_en',
        'hero_subtitle_ar', 'hero_subtitle_en',
        'logo_url',
      ] as const;

      for (const field of optionalFields) {
        if (formData[field]) payload[field] = formData[field];
        else if (isEdit) payload[field] = null;
      }

      // Suggested questions: split textarea on newlines, trim, drop blanks.
      const parseQuestions = (raw: string) =>
        raw.split('\n').map(s => s.trim()).filter(Boolean);
      const sqAr = parseQuestions(formData.suggested_questions_ar_text);
      const sqEn = parseQuestions(formData.suggested_questions_en_text);
      payload.suggested_questions_ar = sqAr.length ? sqAr : (isEdit ? null : undefined);
      payload.suggested_questions_en = sqEn.length ? sqEn : (isEdit ? null : undefined);
      payload.hosted_page_enabled = formData.hosted_page_enabled;

      // Handoff contact — only send when at least one field is filled.
      const hasHandoff = formData.handoff_unit_ar || formData.handoff_unit_en
        || formData.handoff_phone || formData.handoff_email;
      payload.handoff_contact = hasHandoff ? {
        unit_ar: formData.handoff_unit_ar || null,
        unit_en: formData.handoff_unit_en || null,
        phone: formData.handoff_phone || null,
        email: formData.handoff_email || null,
      } : (isEdit ? null : undefined);

      if (formData.faculty_code) {
        payload.faculty_code = parseInt(formData.faculty_code);
      } else if (isEdit) {
        payload.faculty_code = null;
      }

      if (isEdit && id) {
        await updateDomain(parseInt(id), payload);
        alert('Domain updated successfully');
      } else {
        await createDomain(payload);
        alert('Domain created successfully');
      }

      navigate('/data/domains');
    } catch (error: any) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const widgetUrl = formData.slug
    ? `${WIDGET_BASE_URL}?domain=${encodeURIComponent(formData.slug)}&lang=ar`
    : '';
  const hostedUrl = formData.slug ? `${HOSTED_BASE_URL}/${encodeURIComponent(formData.slug)}` : '';

  const handleCopyUrl = async () => {
    if (!widgetUrl) return;
    try {
      await navigator.clipboard.writeText(widgetUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = widgetUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-primary-900 tracking-tight leading-tight">
          {isEdit ? 'Edit Domain' : 'Create Domain'}
        </h1>
        <Link to="/data/domains" className="btn-ghost text-xs gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card p-6 space-y-5">
          <h2 className="text-sm font-semibold text-primary-700 uppercase tracking-wider">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">English Name <span className="text-error">*</span></label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => handleNameEnChange(e.target.value)}
                required
                className="input-base"
                placeholder="Domain Name in English"
              />
            </div>
            <div>
              <label className="label">Arabic Name <span className="text-error">*</span></label>
              <input
                type="text"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                required
                className="input-base"
                placeholder="اسم النطاق بالعربية"
              />
            </div>
          </div>

          <div>
            <label className="label">Slug <span className="text-error">*</span></label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => {
                setSlugManuallyEdited(true);
                setFormData({ ...formData, slug: e.target.value });
              }}
              required
              className="input-base font-mono text-sm"
              placeholder="domain-slug"
            />
            <p className="text-xs text-primary-400 mt-1">Auto-generated from English name. Edit to customize.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Faculty Code</label>
              <input
                type="number"
                value={formData.faculty_code}
                onChange={(e) => setFormData({ ...formData, faculty_code: e.target.value })}
                className="input-base"
                placeholder="Optional faculty code"
              />
            </div>
            <div>
              <label className="label">Status</label>
              <div className="flex items-center gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.is_active ? 'bg-emerald-500' : 'bg-primary-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-primary-600">
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* System Prompt Overrides */}
        <div className="card p-6 space-y-5">
          <h2 className="text-sm font-semibold text-primary-700 uppercase tracking-wider">System Prompt Overrides</h2>

          <div>
            <label className="label">System Prompt Override (English)</label>
            <textarea
              value={formData.system_prompt_override_en}
              onChange={(e) => setFormData({ ...formData, system_prompt_override_en: e.target.value })}
              className="input-base min-h-[100px]"
              placeholder="Custom system prompt for this domain (English)..."
              rows={4}
            />
          </div>

          <div>
            <label className="label">System Prompt Override (Arabic)</label>
            <textarea
              value={formData.system_prompt_override_ar}
              onChange={(e) => setFormData({ ...formData, system_prompt_override_ar: e.target.value })}
              className="input-base min-h-[100px]"
              placeholder="تعليمات النظام المخصصة لهذا النطاق..."
              rows={4}
            />
          </div>
        </div>

        {/* Widget Configuration */}
        <div className="card p-6 space-y-5">
          <h2 className="text-sm font-semibold text-primary-700 uppercase tracking-wider">Widget Configuration</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Widget Title (English)</label>
              <input
                type="text"
                value={formData.widget_title_en}
                onChange={(e) => setFormData({ ...formData, widget_title_en: e.target.value })}
                className="input-base"
                placeholder="Chat with us"
              />
            </div>
            <div>
              <label className="label">Widget Title (Arabic)</label>
              <input
                type="text"
                value={formData.widget_title_ar}
                onChange={(e) => setFormData({ ...formData, widget_title_ar: e.target.value })}
                className="input-base"
                placeholder="تحدث معنا"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Widget Subtitle (English)</label>
              <input
                type="text"
                value={formData.widget_subtitle_en}
                onChange={(e) => setFormData({ ...formData, widget_subtitle_en: e.target.value })}
                className="input-base"
                placeholder="We're here to help"
              />
            </div>
            <div>
              <label className="label">Widget Subtitle (Arabic)</label>
              <input
                type="text"
                value={formData.widget_subtitle_ar}
                onChange={(e) => setFormData({ ...formData, widget_subtitle_ar: e.target.value })}
                className="input-base"
                placeholder="نحن هنا لمساعدتك"
              />
            </div>
          </div>

          <div>
            <label className="label">Primary Color</label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="input-base font-mono text-sm flex-1"
                placeholder="#8e8ea0"
                maxLength={7}
              />
              {formData.primary_color && /^#[0-9a-fA-F]{6}$/.test(formData.primary_color) && (
                <div
                  className="w-10 h-10 rounded-lg border border-primary-200 flex-shrink-0"
                  style={{ backgroundColor: formData.primary_color }}
                />
              )}
            </div>
            <p className="text-xs text-primary-400 mt-1">Hex color code (e.g. #8e8ea0)</p>
          </div>
        </div>

        {/* Hosted Public Page */}
        <div className="card p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-primary-700 uppercase tracking-wider">Hosted Public Page</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-primary-500">
                {formData.hosted_page_enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, hosted_page_enabled: !formData.hosted_page_enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.hosted_page_enabled ? 'bg-emerald-500' : 'bg-primary-300'
                }`}
                aria-label="Toggle hosted page"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.hosted_page_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          <p className="text-xs text-primary-400 -mt-3">
            A full-screen branded chat page at <code className="font-mono">/c/{`{slug}`}</code>. Different from the embeddable widget — share this URL directly with clients or end-users.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Hero Title (English)</label>
              <input
                type="text"
                value={formData.hero_title_en}
                onChange={(e) => setFormData({ ...formData, hero_title_en: e.target.value })}
                className="input-base"
                placeholder="Ask the HR assistant anything"
              />
            </div>
            <div>
              <label className="label">Hero Title (Arabic)</label>
              <input
                type="text"
                value={formData.hero_title_ar}
                onChange={(e) => setFormData({ ...formData, hero_title_ar: e.target.value })}
                className="input-base"
                placeholder="اسأل مساعد الموارد البشرية"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Hero Subtitle (English)</label>
              <textarea
                value={formData.hero_subtitle_en}
                onChange={(e) => setFormData({ ...formData, hero_subtitle_en: e.target.value })}
                className="input-base min-h-[68px]"
                placeholder="Answers grounded in HR policies and procedures."
                rows={2}
              />
            </div>
            <div>
              <label className="label">Hero Subtitle (Arabic)</label>
              <textarea
                value={formData.hero_subtitle_ar}
                onChange={(e) => setFormData({ ...formData, hero_subtitle_ar: e.target.value })}
                className="input-base min-h-[68px]"
                placeholder="إجابات مستندة إلى سياسات وإجراءات الموارد البشرية."
                rows={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Suggested Questions (English)</label>
              <textarea
                value={formData.suggested_questions_en_text}
                onChange={(e) => setFormData({ ...formData, suggested_questions_en_text: e.target.value })}
                className="input-base min-h-[110px] font-normal"
                placeholder={'One per line\nE.g.: How many annual leave days?\nWhat is the sick leave policy?'}
                rows={5}
              />
              <p className="text-xs text-primary-400 mt-1">One question per line. Up to 6 shown.</p>
            </div>
            <div>
              <label className="label">Suggested Questions (Arabic)</label>
              <textarea
                value={formData.suggested_questions_ar_text}
                onChange={(e) => setFormData({ ...formData, suggested_questions_ar_text: e.target.value })}
                className="input-base min-h-[110px]"
                placeholder={'سؤال واحد في كل سطر\nمثال: ما هي مدة الإجازة السنوية؟\nما هي سياسة الإجازة المرضية؟'}
                rows={5}
                dir="rtl"
              />
              <p className="text-xs text-primary-400 mt-1">سؤال واحد في كل سطر. حتى 6 أسئلة.</p>
            </div>
          </div>

          <div>
            <label className="label">Logo URL</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              className="input-base"
              placeholder="https://example.com/logo.png"
            />
            <p className="text-xs text-primary-400 mt-1">Optional. Falls back to the first letter of the domain name.</p>
          </div>

          <div className="pt-4 border-t border-primary-100 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-primary-700">When the bot can't answer</h3>
              <p className="text-xs text-primary-400 mt-1">
                Contact card shown under "I couldn't find this" answers, so users always have a next step.
                Leave empty to fall back to the general university contact.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Department / Unit (English)</label>
                <input
                  type="text"
                  value={formData.handoff_unit_en}
                  onChange={(e) => setFormData({ ...formData, handoff_unit_en: e.target.value })}
                  className="input-base"
                  placeholder="Human Resources Department"
                />
              </div>
              <div>
                <label className="label">Department / Unit (Arabic)</label>
                <input
                  type="text"
                  value={formData.handoff_unit_ar}
                  onChange={(e) => setFormData({ ...formData, handoff_unit_ar: e.target.value })}
                  className="input-base"
                  placeholder="دائرة الموارد البشرية"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="label">Phone</label>
                <input
                  type="text"
                  value={formData.handoff_phone}
                  onChange={(e) => setFormData({ ...formData, handoff_phone: e.target.value })}
                  className="input-base"
                  placeholder="+970 9 234 5113"
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.handoff_email}
                  onChange={(e) => setFormData({ ...formData, handoff_email: e.target.value })}
                  className="input-base"
                  placeholder="department@najah.edu"
                />
              </div>
            </div>
          </div>

          {hostedUrl && formData.hosted_page_enabled && (
            <div className="flex items-center gap-3 pt-2 border-t border-primary-100">
              <code className="flex-1 text-sm bg-primary-50 border border-primary-200 rounded-lg px-4 py-2.5 text-primary-700 break-all">
                {hostedUrl}
              </code>
              <a
                href={hostedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-xs gap-1.5 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                Open page
              </a>
            </div>
          )}
        </div>

        {/* Widget URL & QR Code */}
        {formData.slug && (
          <div className="card p-6 space-y-4">
            <h2 className="text-sm font-semibold text-primary-700 uppercase tracking-wider">Widget URL</h2>

            <div className="flex items-center gap-3">
              <code className="flex-1 text-sm bg-primary-50 border border-primary-200 rounded-lg px-4 py-2.5 text-primary-700 break-all">
                {widgetUrl}
              </code>
              <button
                type="button"
                onClick={handleCopyUrl}
                className="btn-secondary text-xs gap-1.5 flex-shrink-0"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                    </svg>
                    Copy URL
                  </>
                )}
              </button>
            </div>

            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-primary-700">QR Code</p>
                  <p className="text-xs text-primary-500 mt-0.5">
                    Generate a QR code for this widget URL using any QR code generator service, or scan the URL directly from the address above.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="btn-primary gap-1.5">
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Saving...
              </>
            ) : (isEdit ? 'Update Domain' : 'Create Domain')}
          </button>
          <Link to="/data/domains" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default DomainForm;
